import React, { useState, useEffect } from "react";
import { FaTimes, FaChevronDown, FaChevronRight } from "react-icons/fa";
import useEscapeClose from "../../../hooks/useEscapeClose";
import { isSalon } from "../../../utils/businessType";

function ChargeModal({ show, onClose, onSave, charge, isEditMode }) {

    // Esc closes this modal (src/hooks/useEscapeClose.js).
    useEscapeClose(onClose);

    // A salon bills every visit at the counter — no dine-in, takeaway or
    // delivery, and no waiter. Its charges apply to every bill, so the
    // service-type and "start ticked on" choices are hidden and saved as all-on.
    const salon = isSalon();

    const initial = {
        charge_name: "",
        description: "",
        charge_type: "Fixed",
        // 'Charge' | 'Tax' | 'Service'. GST and the service charge are charge
        // rows like any other — the role only decides which line of the bill
        // they land on, and keeps tax separable for GST reporting.
        charge_role: "Charge",
        amount: "",
        auto_apply: false,
        // "Apply to all, but the cashier can drop it" — only for an auto-applied
        // ordinary charge. It reaches the bill as a pre-selected, removable chip.
        removable: false,
        // For a removable extra, where it STARTS ticked. Cashier on, waiter off.
        preselect_cashier: true,
        preselect_waiter: false,
        applies_dinein: true,
        applies_takeaway: false,
        applies_delivery: false,
        apply_tax: true,
        status: "Active"
    };

    const [form, setForm] = useState(initial);
    const [errors, setErrors] = useState({});
    const [showAdvanced, setShowAdvanced] = useState(false);

    useEffect(() => {
        if (isEditMode && charge) {
            setForm({
                charge_name: charge.charge_name || "",
                description: charge.description || "",
                charge_type: charge.charge_type || "Fixed",
                charge_role: charge.charge_role || "Charge",
                amount: charge.amount || "",
                auto_apply: Boolean(Number(charge.auto_apply)),
                removable: Boolean(Number(charge.removable)),
                // A row saved before this feature has no columns yet: cashier
                // defaults on, waiter off — the same as the DB defaults.
                preselect_cashier: charge.preselect_cashier === undefined || charge.preselect_cashier === null
                    ? true : Boolean(Number(charge.preselect_cashier)),
                preselect_waiter: Boolean(Number(charge.preselect_waiter)),
                applies_dinein: Boolean(charge.applies_dinein),
                applies_takeaway: Boolean(charge.applies_takeaway),
                applies_delivery: Boolean(charge.applies_delivery),
                apply_tax: Boolean(charge.apply_tax),
                status: charge.status || "Active"
            });
        } else {
            setForm(initial);
        }
        setErrors({});
        setShowAdvanced(false);
        // eslint-disable-next-line
    }, [charge, isEditMode, show]);

    const isTaxLike = form.charge_role !== "Charge";

    const validate = () => {
        const e = {};
        if (!form.charge_name.trim()) e.charge_name = "Charge name is required.";
        if (!form.amount || Number(form.amount) <= 0) e.amount = "Enter a valid amount.";
        if (!salon && !form.applies_dinein && !form.applies_takeaway && !form.applies_delivery) {
            e.applies = "Select at least one option.";
        }
        // Per-item / per-person / per-hour have no meaning as a tax and would
        // quietly bill the wrong figure, so the backend refuses them too.
        if (isTaxLike && !["Fixed", "Percentage"].includes(form.charge_type)) {
            e.charge_type = "A tax or service charge must be a percentage or a fixed amount.";
        }
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!validate()) return;
        const auto_apply = isTaxLike ? true : form.auto_apply;
        // Removable only makes sense for an auto-applied ordinary charge; GST
        // and service stay locked, and an opt-in chip is already optional.
        const removable = (!isTaxLike && auto_apply) ? form.removable : false;
        onSave({
            ...form,
            amount: Number(form.amount),
            // A tax the cashier could forget to tap is not a tax.
            auto_apply,
            removable,
            // Where a removable extra starts ticked — only meaningful when removable.
            preselect_cashier: removable ? (salon || form.preselect_cashier) : false,
            preselect_waiter: removable ? (!salon && form.preselect_waiter) : false,
            // Salon bills are counter (Takeaway) orders; on for every type so
            // the charge reaches every one of them.
            ...(salon ? { applies_dinein: true, applies_takeaway: true, applies_delivery: true } : {})
        });
    };

    const setRole = (role) => {
        setForm((prev) => ({
            ...prev,
            charge_role: role,
            // Tax and service are all but always a percentage of the bill.
            charge_type: role === "Charge" ? prev.charge_type : "Percentage",
            auto_apply: role === "Charge" ? prev.auto_apply : true
        }));
    };

    const toggleApply = (key) => {
        setForm((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    if (!show) return null;

    return (
        <div className="charge-modal-overlay" onClick={onClose}>
            <div className="charge-modal" onClick={(e) => e.stopPropagation()}>
                <div className="charge-modal-header">
                    <h2>{isEditMode ? "Edit Charge" : "Add New Charge"}</h2>
                    <button className="close-btn" onClick={onClose}><FaTimes /></button>
                </div>
                <p className="charge-modal-subtitle">
                    {isEditMode
                        ? "Update billing charge configuration."
                        : `Create a billing charge for this ${salon ? "salon" : "restaurant"}.`}
                </p>

                <form onSubmit={handleSubmit}>
                    <div className="charge-modal-body">

                        {/* What this charge IS. GST and the service charge are
                            rows in this table too — the role decides which line
                            of the bill they land on and keeps tax separable for
                            GST reporting. */}
                        <div className="charge-form-group">
                            <label>This charge is</label>
                            <div className="applies-cards">
                                <div
                                    className={`applies-card ${form.charge_role === "Charge" ? "selected" : ""}`}
                                    onClick={() => setRole("Charge")}
                                >
                                    🧾 A charge
                                </div>
                                <div
                                    className={`applies-card ${form.charge_role === "Tax" ? "selected" : ""}`}
                                    onClick={() => setRole("Tax")}
                                >
                                    🏛 GST / Tax
                                </div>
                                <div
                                    className={`applies-card ${form.charge_role === "Service" ? "selected" : ""}`}
                                    onClick={() => setRole("Service")}
                                >
                                    🛎 Service Charge
                                </div>
                            </div>
                            <p className="logo-help-text" style={{ marginTop: "6px" }}>
                                {form.charge_role === "Tax"
                                    ? "Adds up into the bill's tax line and the GST figure in Reports. Not registered for GST? Don't create one."
                                    : form.charge_role === "Service"
                                        ? "Adds up into the bill's service-charge line. Delete it to stop levying a service charge."
                                        : salon
                                            ? "Priced on top of the services and listed separately on the bill."
                                            : "Packing, delivery, AC — priced on top of the goods and listed separately on the bill."}
                            </p>
                        </div>

                        {/* Charge Name */}
                        <div className="charge-form-group">
                            <label>Charge Name</label>
                            <input
                                type="text"
                                placeholder={
                                    form.charge_role === "Tax" ? "e.g. GST 5%"
                                        : form.charge_role === "Service" ? "e.g. Service Charge 2%"
                                            : "e.g. AC Charge"
                                }
                                value={form.charge_name}
                                onChange={(e) => setForm({ ...form, charge_name: e.target.value })}
                            />
                            <p className="logo-help-text" style={{ marginTop: "4px" }}>
                                This is printed on the customer's bill exactly as typed.
                            </p>
                            {errors.charge_name && <div className="field-error">{errors.charge_name}</div>}
                        </div>

                        {/* Description */}
                        <div className="charge-form-group">
                            <label>Description <span style={{ fontWeight: 400, color: "#94A3B8" }}>(optional)</span></label>
                            <textarea
                                rows="2"
                                placeholder="Brief description of this charge"
                                value={form.description}
                                onChange={(e) => setForm({ ...form, description: e.target.value })}
                            />
                        </div>

                        {/* Type & Amount */}
                        <div className="charge-form-row">
                            <div className="charge-form-group">
                                <label>Charge Type</label>
                                <select
                                    value={form.charge_type}
                                    onChange={(e) => setForm({ ...form, charge_type: e.target.value })}
                                >
                                    <option value="Fixed">Fixed Amount</option>
                                    <option value="Percentage">Percentage</option>
                                    {!isTaxLike && <option value="Per Item">Per Item</option>}
                                    {!isTaxLike && <option value="Per Person">Per Person</option>}
                                    {!isTaxLike && !salon && <option value="Per Table">Per Table</option>}
                                    {!isTaxLike && <option value="Per Hour">Per Hour</option>}
                                </select>
                                {errors.charge_type && <div className="field-error">{errors.charge_type}</div>}
                            </div>
                            <div className="charge-form-group">
                                <label>{form.charge_type === "Percentage" ? "Rate" : "Amount"}</label>
                                <div className="amount-input-wrap">
                                    <span className="currency-prefix">{form.charge_type === "Percentage" ? "%" : "₹"}</span>
                                    <input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        placeholder="0"
                                        value={form.amount}
                                        onChange={(e) => setForm({ ...form, amount: e.target.value })}
                                    />
                                </div>
                                {errors.amount && <div className="field-error">{errors.amount}</div>}
                            </div>
                        </div>

                        {/* Applies To — a restaurant's service types. */}
                        {!salon && (
                        <div className="charge-form-group">
                            <label>Applies To</label>
                            <div className="applies-cards">
                                <div
                                    className={`applies-card ${form.applies_dinein ? "selected" : ""}`}
                                    onClick={() => toggleApply("applies_dinein")}
                                >
                                    🍽 Dine-in
                                </div>
                                <div
                                    className={`applies-card ${form.applies_takeaway ? "selected" : ""}`}
                                    onClick={() => toggleApply("applies_takeaway")}
                                >
                                    🛍 Takeaway
                                </div>
                                <div
                                    className={`applies-card ${form.applies_delivery ? "selected" : ""}`}
                                    onClick={() => toggleApply("applies_delivery")}
                                >
                                    🚗 Delivery
                                </div>
                            </div>
                            {errors.applies && <div className="field-error">{errors.applies}</div>}
                        </div>
                        )}

                        {/* How it reaches the bill. A tax or service charge is
                            always automatic — one the cashier could forget to
                            tap is not a tax. */}
                        <div className="toggle-row">
                            <span className="toggle-label">
                                Add to every bill automatically
                                <span style={{ display: "block", fontWeight: 400, fontSize: "12px", color: "#94A3B8" }}>
                                    {isTaxLike
                                        ? "Always on for a tax or service charge."
                                        : form.auto_apply
                                            ? "On every matching bill, with nothing for the cashier to do."
                                            : "The cashier taps it on the bill screen when it applies."}
                                </span>
                            </span>
                            <button
                                type="button"
                                className={`toggle-switch ${(isTaxLike || form.auto_apply) ? "on" : ""}`}
                                disabled={isTaxLike}
                                onClick={() => setForm({
                                    ...form,
                                    auto_apply: !form.auto_apply,
                                    // Turning auto off makes "can remove" meaningless.
                                    removable: form.auto_apply ? false : form.removable
                                })}
                            >
                                <span className="toggle-knob" />
                            </button>
                        </div>

                        {/* Only an auto-applied ordinary charge can be removable:
                            it lands on every matching bill already ticked, and the
                            cashier taps it off when it doesn't apply. GST / service
                            stay locked; an opt-in chip is the cashier's to add. */}
                        {!isTaxLike && form.auto_apply && (
                            <div className="toggle-row">
                                <span className="toggle-label">
                                    Cashier can remove it from a bill
                                    <span style={{ display: "block", fontWeight: 400, fontSize: "12px", color: "#94A3B8" }}>
                                        {form.removable
                                            ? "Shown already ticked on the bill screen; the cashier can tap it off."
                                            : "Locked on every matching bill — nobody can take it off."}
                                    </span>
                                </span>
                                <button
                                    type="button"
                                    className={`toggle-switch ${form.removable ? "on" : ""}`}
                                    onClick={() => setForm({ ...form, removable: !form.removable })}
                                >
                                    <span className="toggle-knob" />
                                </button>
                            </div>
                        )}

                        {/* Where a removable extra STARTS ticked. The counter
                            usually adds parcel/packing, so cashier defaults on;
                            the waiter bills what was eaten, so waiter defaults off.
                            Either can be flipped per charge. */}
                        {!salon && !isTaxLike && form.auto_apply && form.removable && (
                            <div className="toggle-row" style={{ paddingLeft: "12px", borderLeft: "2px solid #eef1f6" }}>
                                <span className="toggle-label">
                                    Start ticked on…
                                    <span style={{ display: "block", fontWeight: 400, fontSize: "12px", color: "#94A3B8" }}>
                                        Where it comes up already ticked. Elsewhere it's shown unticked to add when needed. A dine-in table follows "Dine-in tables" whether the waiter or cashier bills it.
                                    </span>
                                </span>
                                <div style={{ display: "flex", gap: "16px", alignItems: "center" }}>
                                    <label style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer", fontSize: "13px", fontWeight: 600 }}>
                                        <input
                                            type="checkbox"
                                            checked={form.preselect_cashier}
                                            onChange={() => setForm({ ...form, preselect_cashier: !form.preselect_cashier })}
                                        />
                                        Counter / takeaway
                                    </label>
                                    <label style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer", fontSize: "13px", fontWeight: 600 }}>
                                        <input
                                            type="checkbox"
                                            checked={form.preselect_waiter}
                                            onChange={() => setForm({ ...form, preselect_waiter: !form.preselect_waiter })}
                                        />
                                        Dine-in tables
                                    </label>
                                </div>
                            </div>
                        )}

                        <div className="toggle-row">
                            <span className="toggle-label">Charge Status</span>
                            <button
                                type="button"
                                className={`toggle-switch ${form.status === "Active" ? "on" : ""}`}
                                onClick={() => setForm({ ...form, status: form.status === "Active" ? "Inactive" : "Active" })}
                            >
                                <span className="toggle-knob" />
                            </button>
                        </div>

                        {/* Advanced Rules */}
                        <button
                            type="button"
                            className="advanced-toggle"
                            onClick={() => setShowAdvanced(!showAdvanced)}
                        >
                            {showAdvanced ? <FaChevronDown /> : <FaChevronRight />}
                            Advanced Rules
                        </button>

                        {showAdvanced && (
                            <div className="advanced-fields">
                                <div className="charge-form-row">
                                    <div className="charge-form-group">
                                        <label>Minimum Bill Amount</label>
                                        <div className="amount-input-wrap">
                                            <span className="currency-prefix">₹</span>
                                            <input type="number" min="0" placeholder="0" />
                                        </div>
                                    </div>
                                    <div className="charge-form-group">
                                        <label>Maximum Charge</label>
                                        <div className="amount-input-wrap">
                                            <span className="currency-prefix">₹</span>
                                            <input type="number" min="0" placeholder="No limit" />
                                        </div>
                                    </div>
                                </div>
                                <div className="charge-form-row">
                                    <div className="charge-form-group">
                                        <label>Start Time</label>
                                        <input type="text" placeholder="e.g. 10:00" />
                                    </div>
                                    <div className="charge-form-group">
                                        <label>End Time</label>
                                        <input type="text" placeholder="e.g. 22:00" />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="charge-modal-footer">
                        <button type="button" className="cancel-btn" onClick={onClose}>Cancel</button>
                        <button type="submit" className="save-btn">
                            {isEditMode ? "Update Charge" : "Save Charge"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default ChargeModal;
